import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  BookOpen,
  LayoutDashboard,
  Users,
  GraduationCap,
  CalendarCheck,
  CalendarRange,
  ClipboardList,
  Clock,
  Award,
  Download,
  Upload,
  LogOut,
  Menu,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/teachers", label: "Teachers", icon: Users, testid: "nav-teachers" },
  { to: "/admin/students", label: "Students", icon: GraduationCap, testid: "nav-students" },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/admin/records", label: "All Records", icon: ClipboardList, testid: "nav-records" },
  { to: "/admin/time-entries", label: "Time Entries", icon: Clock, testid: "nav-time-entries" },
  { to: "/admin/points", label: "Points", icon: Award, testid: "nav-points" },
  { to: "/admin/exports", label: "Exports", icon: Download, testid: "nav-exports" },
];

const SETTINGS_NAV = [
  { to: "/admin/semesters", label: "Semesters", icon: CalendarRange, testid: "nav-semesters" },
  { to: "/admin/import", label: "Import Data", icon: Upload, testid: "nav-import" },
];

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-3 px-6 border-b border-border">
        <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
          <BookOpen className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="leading-tight">
          <p className="font-heading font-semibold text-foreground">Al-Bushra</p>
          <p className="text-xs text-muted-foreground">Quran School</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-[3px] ${
                isActive
                  ? "bg-primary/5 text-primary border-[hsl(var(--gold))]"
                  : "text-muted-foreground border-transparent hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}

        <div className="pt-5 mt-2">
          <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
            Settings
          </p>
          {SETTINGS_NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-[3px] ${
                  isActive
                    ? "bg-primary/5 text-primary border-[hsl(var(--gold))]"
                    : "text-muted-foreground border-transparent hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-card border-r border-border">
        <SidebarContent />
      </aside>

      <div className="lg:pl-64">
        {/* Header */}
        <header className="h-16 sticky top-0 z-30 bg-card border-b border-border flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  data-testid="mobile-menu-button"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SidebarContent onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-accent text-accent-foreground">
              Admin
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">
              {user?.full_name}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="h-9"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
